/**
 * CLE-2786 -- one contract shared by several providers.
 *
 * The contract's own provider is the PRIMARY. `linkedProviderIds` lists the other providers
 * -- usually in other accounts -- that draw their rates from this contract instead of from a
 * hand-maintained duplicate of it. Ani's case: "Ascentist Physician Group" exists as six
 * provider records across six accounts, five of which held byte-identical contracts.
 *
 * Mirrors `services/clear-api/src/domains/_shared/linkedProviders.ts`; keep the two in step.
 */

export interface ILinkedProvider {
  id: string;
  name: string;
  accountId?: string | null;
}

export interface ILinkedProviderOption {
  label: string;
  value: string;
}

export const LINKED_PROVIDERS_COPY = {
  title: "Connected Providers",
  help: "Select other providers that should use this contract's rates instead of holding a duplicate contract of their own.",
  placeholder: "Select connected providers",
  note: "Each connected provider gets this contract's rates under its own name, so estimates and money transfers stay with the right provider. A provider that already has its own active contract for this payer has to have it removed first.",
};

/**
 * Options for the connected-providers picker: every provider except the contract's own.
 * Deliberately NOT filtered to the current account -- linking across accounts is the point.
 */
export const linkedProviderOptions = (
  providers: ILinkedProvider[] | undefined,
  contractProviderId: string | undefined
): ILinkedProviderOption[] =>
  (providers ?? [])
    .filter((p) => p.id !== contractProviderId)
    .map((p) => ({ label: p.name, value: p.id }))
    .sort((a, b) => a.label.localeCompare(b.label));

/**
 * Names of the providers sharing a contract, for the list and detail views. The primary comes
 * first, matching how the contract is stored and how Ani asked to see it ("the primary one
 * with connected names").
 */
export const sharedProviderNames = (
  primaryName: string | undefined,
  linkedProviders: ILinkedProvider[] | undefined
): string[] => [
  ...(primaryName ? [primaryName] : []),
  ...(linkedProviders ?? []).map((p) => p.name),
];

/**
 * Keeps a selection honest when the contract's own provider changes underneath it: the new
 * provider can never also be a connected one.
 */
export const withoutContractProvider = (
  linkedProviderIds: string[] | undefined,
  contractProviderId: string | undefined
): string[] =>
  (linkedProviderIds ?? []).filter((id) => id !== contractProviderId);
